import React, { useEffect, useState, useMemo } from 'react';
import { fetchHistoricalData, fetchDevices, fetchDeviceProperties, DeviceProperty } from '../services/api';
import { Device } from '../types';
import { useAuth } from '../context/AuthContext';
import { formatInTZ } from '../utils/timezone';
import ErrorBanner from './shared/ErrorBanner';
import PageHeader from './shared/PageHeader';
import { ui } from '../styles/shared';

const toLocalDateTimeString = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const defaultDateFrom = () => toLocalDateTimeString(new Date(Date.now() - 24 * 3600 * 1000));
const defaultDateTo = () => toLocalDateTimeString(new Date());

interface DataRow {
    id: number;
    ieee_address: string;
    name: string;
    property: string;
    value_num: number | null;
    value_str: string | null;
    parameter_name: string | null;
    parameter_unit: string | null;
    enum_values: { value: string; text: string }[] | null;
    timestamp: string;
}

type SortField = 'timestamp' | 'name' | 'property' | 'value';
type SortDir = 'asc' | 'desc';

// Stilar från den gemensamma stilfilen (lite mindre text i filterpanelen)
const inputStyle: React.CSSProperties = { ...ui.input, padding: '7px 10px', fontSize: 13 };
const labelStyle: React.CSSProperties = { ...ui.label, fontSize: 12, color: '#555' };

const PROPERTY_LABELS: Record<string, string> = {
    temperature: 'Temperatur (°C)',
    humidity: 'Fukt (%)',
    pressure: 'Lufttryck (hPa)',
    illuminance_lux: 'Ljusstyrka (lux)',
    occupancy: 'Rörelse',
    contact: 'Dörr/fönster',
    state: 'Läge (PÅ/AV)',
    battery: 'Batteri (%)',
    power: 'Effekt (W)',
    energy: 'Energi',
    current: 'Ström (A)',
    voltage: 'Spänning (V)',
    linkquality: 'Signalstyrka',
    'io_channels.ch_112': 'IO-kanal 112 (ingång 1)',
    'io_channels.ch_113': 'IO-kanal 113 (ingång 2)',
    'io_channels.ch_114': 'IO-kanal 114 (ingång 3)',
    'io_channels.ch_115': 'IO-kanal 115 (ingång 4)',
    'io_channels.ch_116': 'IO-kanal 116 (utgång 1)',
    'io_channels.ch_117': 'IO-kanal 117 (utgång 2)',
};

const DataVisualization: React.FC = () => {
    const { timezone } = useAuth();
    const [raw, setRaw] = useState<DataRow[]>([]);
    const [devices, setDevices] = useState<Device[]>([]);
    const [devicePropertiesMap, setDevicePropertiesMap] = useState<Record<string, DeviceProperty[]>>({});
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Server-side filters (applied on fetch)
    const [filterDevice, setFilterDevice] = useState('');
    const [filterProperty, setFilterProperty] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState(defaultDateFrom);
    const [filterDateTo, setFilterDateTo] = useState(defaultDateTo);

    // Client-side filters (applied to already-fetched rows)
    const [filterValueMin, setFilterValueMin] = useState('');
    const [filterValueMax, setFilterValueMax] = useState('');
    const [filterSearch, setFilterSearch] = useState('');

    // Sorting
    const [sortField, setSortField] = useState<SortField>('timestamp');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    // Pagination
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 50;

    const loadData = async (
        devList: Device[],
        device: string,
        property: string,
        dateFrom: string,
        dateTo: string,
    ) => {
        const allIeees = devList.map(d => d.ieee_address).filter(Boolean) as string[];
        const ieees = device ? [device] : allIeees;
        if (ieees.length === 0) { setRaw([]); return; }
        const from = dateFrom ? new Date(dateFrom).toISOString() : new Date(Date.now() - 24 * 3600 * 1000).toISOString();
        const to = dateTo ? new Date(dateTo).toISOString() : new Date().toISOString();
        const props = property ? [property] : undefined;
        setLoading(true);
        try {
            const result = await fetchHistoricalData(ieees, from, to, props);
            setRaw(result);
            setError(null);
        } catch (e: any) {
            setError(e.message || 'Kunde inte hämta data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            setInitializing(true);
            try {
                const devList = await fetchDevices();
                setDevices(devList);
                // Properties are fetched lazily when the user selects a device
            } finally {
                setInitializing(false);
            }
        };
        init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canSearch = !!filterDevice;

    // When a device is selected, ensure its properties are in the map (lazy-fetch if missing)
    useEffect(() => {
        if (!filterDevice) return;
        if (devicePropertiesMap[filterDevice] && devicePropertiesMap[filterDevice].length > 0) return;
        fetchDeviceProperties(filterDevice).then(props => {
            if (props) {
                setDevicePropertiesMap(prev => ({ ...prev, [filterDevice]: props }));
            }
        });
    }, [filterDevice]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = () => {
        if (!canSearch) return;
        loadData(devices, filterDevice, filterProperty, filterDateFrom, filterDateTo);
        setPage(1);
    };

    // Device dropdown options
    const deviceOptions = useMemo(() => {
        return devices
            .filter(d => d.ieee_address)
            .map(d => [d.ieee_address!, d.name] as [string, string])
            .sort((a, b) => a[1].localeCompare(b[1]));
    }, [devices]);

    // Property dropdown options from devicePropertiesMap
    const propertyOptions = useMemo(() => {
        const seen = new Map<string, string>();
        const source = filterDevice
            ? (devicePropertiesMap[filterDevice] || [])
            : Object.values(devicePropertiesMap).flat();
        source.forEach(p => {
            if (!seen.has(p.key)) seen.set(p.key, p.label || p.key);
        });
        return [...seen.entries()]
            .map(([key, label]) => ({ key, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [devicePropertiesMap, filterDevice]);

    // Property label lookup
    const propertyLabels = useMemo(() => {
        const map: Record<string, string> = { ...PROPERTY_LABELS };
        Object.values(devicePropertiesMap).flat().forEach(p => {
            if (p.label) map[p.key] = p.label;
        });
        raw.forEach(r => { if (r.parameter_name && !map[r.property]) map[r.property] = r.parameter_name; });
        return map;
    }, [devicePropertiesMap, raw]);

    // Client-side filtering (text search and value range only — device/property/date are server-side)
    const filtered = useMemo(() => {
        let rows = raw;
        if (filterSearch) {
            const q = filterSearch.toLowerCase();
            rows = rows.filter(r =>
                r.name.toLowerCase().includes(q) ||
                r.property.toLowerCase().includes(q) ||
                String(r.value_num ?? r.value_str ?? '').toLowerCase().includes(q)
            );
        }
        if (filterValueMin !== '') {
            const min = parseFloat(filterValueMin);
            rows = rows.filter(r => r.value_num !== null && r.value_num >= min);
        }
        if (filterValueMax !== '') {
            const max = parseFloat(filterValueMax);
            rows = rows.filter(r => r.value_num !== null && r.value_num <= max);
        }
        return rows;
    }, [raw, filterSearch, filterValueMin, filterValueMax]);

    // Sorting
    const sorted = useMemo(() => {
        const rows = [...filtered];
        rows.sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'timestamp':
                    cmp = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
                    break;
                case 'name':
                    cmp = a.name.localeCompare(b.name);
                    break;
                case 'property':
                    cmp = a.property.localeCompare(b.property);
                    break;
                case 'value':
                    cmp = (a.value_num !== null && b.value_num !== null)
                        ? a.value_num - b.value_num
                        : String(a.value_num ?? a.value_str ?? '').localeCompare(String(b.value_num ?? b.value_str ?? ''));
                    break;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [filtered, sortField, sortDir]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const toggleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('desc');
        }
        setPage(1);
    };

    const resetFilters = () => {
        setFilterDevice('');
        setFilterProperty('');
        setFilterDateFrom(defaultDateFrom());
        setFilterDateTo(defaultDateTo());
        setFilterValueMin('');
        setFilterValueMax('');
        setFilterSearch('');
        setPage(1);
        setRaw([]);
    };

    const SortHeader: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
        <th onClick={() => toggleSort(field)} style={{
            padding: '10px 12px', textAlign: 'left', cursor: 'pointer',
            userSelect: 'none', whiteSpace: 'nowrap',
            backgroundColor: sortField === field ? '#e8f0fe' : '#f8f9fa',
            borderBottom: '2px solid #ddd'
        }}>
            {label} {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ color: '#ccc' }}>⇅</span>}
        </th>
    );

    const formatValue = (property: string, value: any): string => {
        if (typeof value === 'boolean') return value ? 'ja' : 'nej';
        if (property === 'temperature') return `${value} °C`;
        if (property === 'humidity') return `${value} %`;
        if (property === 'pressure') return `${value} hPa`;
        if (property === 'illuminance_lux') return `${value} lux`;
        if (property === 'battery') return `${value} %`;
        if (property === 'power') return `${value} W`;
        if (property === 'current') return `${value} A`;
        if (property === 'voltage') return `${value} V`;
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    const propertyColor = (property: string): string => {
        const map: Record<string, string> = {
            temperature: '#ff6b35', humidity: '#4a90d9', pressure: '#7a5af8',
            battery: '#28a745', linkquality: '#6c757d', illuminance_lux: '#f0ad00',
            power: '#e91e63', state: '#009688', occupancy: '#795548', contact: '#607d8b'
        };
        return map[property] || '#333';
    };

    const activeFiltersCount = [filterDevice, filterProperty, filterValueMin, filterValueMax, filterSearch].filter(Boolean).length;

    return (
        <div>
            <PageHeader icon="📈" title="Mätvärden"
                description={initializing ? 'Hämtar enheter…' : loading ? 'Hämtar data…' : raw.length > 0 ? `${filtered.length.toLocaleString()} mätvärden${activeFiltersCount > 0 ? ` (av ${raw.length.toLocaleString()})` : ' totalt'}` : 'Välj en enhet och tryck på Visa för att hämta data.'}
                actions={
                    <button onClick={handleSearch} disabled={loading || initializing || !canSearch} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: canSearch ? 'pointer' : 'default', opacity: canSearch ? 1 : 0.5 }}>
                        ↺ Uppdatera
                    </button>
                }
            />

            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            {/* Filter Panel */}
            <div style={{ backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px' }}>
                        🔍 Filter {activeFiltersCount > 0 && (
                            <span style={{ backgroundColor: '#007bff', color: 'white', borderRadius: '10px', padding: '1px 8px', fontSize: '12px', marginLeft: '8px' }}>
                                {activeFiltersCount} aktiva
                            </span>
                        )}
                    </h3>
                    {activeFiltersCount > 0 && (
                        <button onClick={resetFilters} style={{ padding: '4px 12px', border: '1px solid #dc3545', color: '#dc3545', backgroundColor: 'white', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                            ✕ Rensa alla
                        </button>
                    )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                    <div>
                        <label style={labelStyle}>Sök</label>
                        <input style={inputStyle} placeholder="Enhet, mätvärde, värde…"
                            value={filterSearch} onChange={e => { setFilterSearch(e.target.value); setPage(1); }} />
                    </div>
                    <div>
                        <label style={labelStyle}>Enhet</label>
                        <select style={inputStyle} value={filterDevice} onChange={e => { setFilterDevice(e.target.value); setFilterProperty(''); setPage(1); }}>
                            <option value="">Alla enheter</option>
                            {deviceOptions.map(([ieee, name]) => (
                                <option key={ieee} value={ieee}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={labelStyle}>Mätvärde</label>
                        <select style={inputStyle} value={filterProperty} onChange={e => { setFilterProperty(e.target.value); setPage(1); }}>
                            <option value="">Alla mätvärden</option>
                            {propertyOptions.map(p => (
                                <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={labelStyle}>Från</label>
                        <input style={inputStyle} type="datetime-local" value={filterDateFrom}
                            onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }} />
                    </div>
                    <div>
                        <label style={labelStyle}>Till</label>
                        <input style={inputStyle} type="datetime-local" value={filterDateTo}
                            onChange={e => { setFilterDateTo(e.target.value); setPage(1); }} />
                    </div>
                    <div>
                        <label style={labelStyle}>Lägsta värde</label>
                        <input style={inputStyle} type="number" step="any" placeholder="t.ex. 20"
                            value={filterValueMin} onChange={e => { setFilterValueMin(e.target.value); setPage(1); }} />
                    </div>
                    <div>
                        <label style={labelStyle}>Högsta värde</label>
                        <input style={inputStyle} type="number" step="any" placeholder="t.ex. 35"
                            value={filterValueMax} onChange={e => { setFilterValueMax(e.target.value); setPage(1); }} />
                    </div>
                </div>
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button onClick={handleSearch} disabled={loading || initializing || !canSearch}
                        style={{ padding: '8px 20px', backgroundColor: canSearch ? '#007bff' : '#aaa', color: 'white', border: 'none', borderRadius: '4px', cursor: canSearch ? 'pointer' : 'default', fontWeight: 'bold' }}>
                        🔍 Visa
                    </button>
                    <span style={{ fontSize: '12px', color: canSearch ? '#888' : '#c00' }}>
                        {canSearch
                            ? 'Enhet, mätvärde och datum hämtas från servern. Värde och sökning filtrerar det som redan hämtats.'
                            : 'Välj en enhet först.'}
                    </span>
                </div>
            </div>

            {/* Summary chips */}
            {!loading && filtered.length > 0 && (() => {
                const byProp: Record<string, number[]> = {};
                filtered.forEach(r => {
                    // Skip enum parameters — averaging an index like 15003 is meaningless
                    if (r.value_num !== null && !(r.enum_values && r.enum_values.length > 0)) {
                        if (!byProp[r.property]) byProp[r.property] = [];
                        byProp[r.property].push(r.value_num);
                    }
                });
                const entries = Object.entries(byProp).filter(([, vals]) => vals.length > 0).slice(0, 6);
                if (entries.length === 0) return null;
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                        {entries.map(([prop, vals]) => {
                            const min = Math.min(...vals), max = Math.max(...vals);
                            const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
                            return (
                                <div key={prop} onClick={() => { setFilterProperty(prop); setPage(1); }}
                                    style={{
                                        backgroundColor: 'white', border: `2px solid ${propertyColor(prop)}`,
                                        borderRadius: '8px', padding: '10px 16px', cursor: 'pointer',
                                        minWidth: '140px', transition: 'box-shadow 0.15s',
                                        boxShadow: filterProperty === prop ? `0 0 0 3px ${propertyColor(prop)}44` : 'none'
                                    }}>
                                    <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {propertyLabels[prop] || PROPERTY_LABELS[prop] || prop}
                                    </div>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: propertyColor(prop) }}>
                                        {avg.toFixed(1)}
                                        <span style={{ fontSize: '11px', color: '#888', fontWeight: 'normal', marginLeft: '4px' }}>snitt</span>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        {min.toFixed(1)} – {max.toFixed(1)} · {vals.length} värden
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            })()}

            {/* Data Table */}
            <div style={{ backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Hämtar data…</div>
                ) : !filterDevice ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Välj en enhet ovanför och tryck på <strong>Visa</strong>.</div>
                ) : sorted.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Inga värden matchar filtret.</div>
                ) : (
                    <>
                        <div className="tbl-scroll">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <SortHeader field="timestamp" label="Tidpunkt" />
                                    <SortHeader field="name" label="Enhet" />
                                    <SortHeader field="property" label="Mätvärde" />
                                    <SortHeader field="value" label="Värde" />
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((row, i) => (
                                    <tr key={`${row.id}-${row.property}`}
                                        style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}
                                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0f7ff')}
                                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? 'white' : '#fafafa')}>
                                        <td style={{ padding: '9px 12px', fontSize: '13px', color: '#555', whiteSpace: 'nowrap' }}>
                                            {formatInTZ(row.timestamp, timezone)}
                                        </td>
                                        <td style={{ padding: '9px 12px' }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{row.name}</div>
                                            <div style={{ fontSize: '11px', color: '#aaa', fontFamily: 'monospace' }}>{row.ieee_address}</div>
                                        </td>
                                        <td style={{ padding: '9px 12px' }}>
                                            <span style={{
                                                display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
                                                backgroundColor: `${propertyColor(row.property)}18`,
                                                color: propertyColor(row.property),
                                                fontSize: '13px', fontWeight: 'bold'
                                            }}>
                                                {propertyLabels[row.property] || PROPERTY_LABELS[row.property] || row.property}
                                            </span>
                                        </td>
                                        <td style={{ padding: '9px 12px', fontWeight: 'bold', fontSize: '15px', color: propertyColor(row.property) }}>
                                            {row.enum_values && row.enum_values.length > 0 && row.value_num !== null
                                                ? (row.enum_values.find(e => Number(e.value) === Number(row.value_num))?.text ?? formatValue(row.property, row.value_num))
                                                : formatValue(row.property, row.value_num !== null ? row.value_num : row.value_str)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                        {totalPages > 1 && (
                            <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '12px 16px', borderTop: '1px solid #eee', backgroundColor: '#fafafa'
                            }}>
                                <span style={{ fontSize: '13px', color: '#666' }}>
                                    Sida {page} av {totalPages} · visar {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} av {sorted.length.toLocaleString()}
                                </span>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button onClick={() => setPage(1)} disabled={page === 1}
                                        style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1, backgroundColor: 'white' }}>
                                        ««
                                    </button>
                                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                        style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1, backgroundColor: 'white' }}>
                                        ‹ Föregående
                                    </button>
                                    {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                        const p = Math.max(1, Math.min(page - 3, totalPages - 6)) + i;
                                        return (
                                            <button key={p} onClick={() => setPage(p)}
                                                style={{
                                                    padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer',
                                                    backgroundColor: p === page ? '#007bff' : 'white',
                                                    color: p === page ? 'white' : '#333', fontWeight: p === page ? 'bold' : 'normal'
                                                }}>
                                                {p}
                                            </button>
                                        );
                                    })}
                                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                        style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1, backgroundColor: 'white' }}>
                                        Nästa ›
                                    </button>
                                    <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                                        style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1, backgroundColor: 'white' }}>
                                        »»
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DataVisualization;
